import { ChallengeService } from '@duck/api-definitions'
import { useTar, type TranslationKey } from '@/modules'

type ActionButton = { text: TranslationKey; className: string }

const ACTION_BUTTON_MAP: Record<ChallengeService.AvailableChallengeStatusEnum, ActionButton> = {
  [ChallengeService.AvailableChallengeStatusEnum.Available]: {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Unbooted]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Ongoing]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Cooldown]: {
    text: 'challenges.retakeInTIME',
    className: 'bg-accent/20 hover:bg-accent/30'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Finished]: {
    text: 'challenges.retake',
    className: 'bg-success hover:bg-success/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Aborted]: {
    text: 'challenges.resume',
    className: 'bg-warning hover:bg-warning/80'
  }
}

const getActionButton = (status: ChallengeService.AvailableChallengeStatusEnum): ActionButton => {
  return ACTION_BUTTON_MAP[status] || {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  }
}

const formatCooldownTime = (minutes: number): [TranslationKey, { x?: number }] => {
  const days = minutes / 24 / 60
  const hours = minutes / 60

  if (days > 2) {
    return ['times.xDays', { x: Math.round(days) }]
  }
  if (hours > 1) {
    return ['times.xHours', { x: Math.round(hours) }]
  }
  // If one hour or less, show minutes
  return minutes === 1
    ? ['times.oneMinute', {}]
    : ['times.xMinutes', { x: Math.round(minutes) }]
}

/**
 * The status-dependent main action of a challenge (Start / Continue / Resume /
 * Retake / Retake-in-<time>), shared by ChallengeCard and the detail drawer so
 * their CTAs never drift apart.
 *
 * `label` is fully translated (cooldown remaining time already interpolated);
 * `className` carries the status background/hover classes; `disabled` is true
 * during cooldown. `showsCooldownTime` tells icon-bearing buttons to drop the
 * icon while the label is the long "Retake in …" form.
 */
export const useChallengeAction = (challenge: ChallengeService.AvailableChallenge) => {
  const { t } = useTar()
  const action = getActionButton(challenge.status)
  const text: TranslationKey =
    challenge.isSandbox === true && challenge.status === ChallengeService.AvailableChallengeStatusEnum.Available
      ? 'challenges.openSandbox'
      : action.text
  const { className } = action

  const cooldownTimeText =
    challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown && challenge.cooldownRemainingTime
      ? t(...formatCooldownTime(challenge.cooldownRemainingTime))
      : null

  return {
    label: cooldownTimeText !== null ? t(text, { time: cooldownTimeText }) : t(text),
    className,
    disabled: challenge.status === ChallengeService.AvailableChallengeStatusEnum.Cooldown,
    showsCooldownTime: cooldownTimeText !== null,
  }
}
